import { useEffect, useRef } from 'react';

import {
  BpkBox,
  BpkSpacing,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkPagination from '@skyscanner-internal/backpack-web/bpk-component-pagination';

import FlightEntryList from '../FlightEntryList';

import type { FlightEntryData } from '@skyscanner-internal/landing-pages-shared-types/types-shared/ArrivalsDepartures';

interface FlightEntryPaginationProps {
  entries: FlightEntryData[];
  error?: boolean;
  onRetryButtonClick?: () => void;
  currentPage?: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
  showPagination: boolean;
  loaded: boolean;
  isFiltering?: boolean;
  showFlightTrackerLinks?: boolean;
  flightTrackerBasePath?: string;
  useUMSFlightTrackerUrls?: boolean;
}

const FlightEntryPagination = ({
  currentPage = 0,
  entries,
  error = undefined,
  flightTrackerBasePath = undefined,
  isFiltering = false,
  loaded,
  onPageChange,
  onRetryButtonClick = undefined,
  pageSize = 20,
  showFlightTrackerLinks = false,
  showPagination,
  useUMSFlightTrackerUrls = false,
}: FlightEntryPaginationProps) => {
  const pageCount = Math.ceil(entries.length / pageSize);
  const pageSliceStart = currentPage * pageSize;
  const pageSliceEnd = pageSliceStart + pageSize;

  const listRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (showFlightTrackerLinks)
      listRef.current?.querySelector<HTMLElement>('a')?.focus();
  }, [currentPage, showFlightTrackerLinks]);

  return (
    <>
      <div ref={listRef}>
        <FlightEntryList
          entries={entries.slice(pageSliceStart, pageSliceEnd)}
          error={error}
          onRetryButtonClick={onRetryButtonClick}
          loaded={loaded}
          isFiltering={isFiltering}
          showFlightTrackerLinks={showFlightTrackerLinks}
          flightTrackerBasePath={flightTrackerBasePath}
          useUMSFlightTrackerUrls={useUMSFlightTrackerUrls}
        />
      </div>
      {showPagination && entries.length !== 0 && (
        // Removed the --bpk-button-primary-on-dark-background-color override: the installed
        // BpkPagination reads --bpk-private-button-colour-bg-primary-on-dark instead, so it
        // had no effect (verified pixel-identical with and without). data-testid is a test hook.
        <BpkBox
          data-testid="flight-entry-pagination"
          paddingTop={BpkSpacing.LG}
          paddingBottom={BpkSpacing.LG}
          paddingStart={BpkSpacing.MD}
          paddingEnd={BpkSpacing.MD}
        >
          <BpkPagination
            pageCount={pageCount}
            selectedPageIndex={currentPage}
            onPageChange={onPageChange}
            previousLabel="previous"
            nextLabel="next"
            visibleRange={3}
            pageLabel={(page: number) => `page ${page}`}
            paginationLabel="default"
          />
        </BpkBox>
      )}
    </>
  );
};

export default FlightEntryPagination;
